import { useEffect, useMemo, useRef, useState } from 'react';
import { IconCalendar, IconChevronDown } from './icons';

/**
 * "Created" date filter for the leads table — a range, or a single day.
 *
 * A popover rather than two bare inputs in the filter row: most people reach
 * for a preset ("today", "last 7 days") rather than picking two calendar
 * dates, so presets are the fast path and the custom pickers are one click
 * further in, not competing for space in the row itself.
 * Dates are read and written as plain `YYYY-MM-DD` in the browser's local
 * calendar — day boundaries are then expanded to that day's start/end before
 * being sent to the API, matching how a person picking "14 Aug" means their
 * own day, not a UTC one.
 */

export interface DateRange {
  from?: string;
  to?: string;
  dateField?: 'created' | 'assigned';
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fromDateInput(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function startOfDayIso(dateInput: string): string {
  const d = fromDateInput(dateInput);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function endOfDayIso(dateInput: string): string {
  const d = fromDateInput(dateInput);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

const PRESETS: Array<{ key: string; label: string; range: () => [string, string] }> = [
  { key: 'today', label: 'Today', range: () => { const t = toDateInput(today()); return [t, t]; } },
  { key: 'yesterday', label: 'Yesterday', range: () => { const t = toDateInput(addDays(today(), -1)); return [t, t]; } },
  { key: '7d', label: 'Last 7 days', range: () => [toDateInput(addDays(today(), -6)), toDateInput(today())] },
  { key: '30d', label: 'Last 30 days', range: () => [toDateInput(addDays(today(), -29)), toDateInput(today())] },
  {
    key: 'this_month',
    label: 'This month',
    range: () => {
      const t = today();
      return [toDateInput(new Date(t.getFullYear(), t.getMonth(), 1)), toDateInput(t)];
    },
  },
  {
    key: 'last_month',
    label: 'Last month',
    range: () => {
      const t = today();
      const start = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const end = new Date(t.getFullYear(), t.getMonth(), 0);
      return [toDateInput(start), toDateInput(end)];
    },
  },
];

function formatShort(dateInput: string): string {
  return fromDateInput(dateInput).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function describe(value: DateRange): { label: string; presetKey: string | null } {
  const prefix = value.dateField === 'assigned' ? 'Assigned' : '';
  if (!value.from && !value.to) return { label: value.dateField === 'assigned' ? 'All assigned dates' : 'All dates', presetKey: null };

  const fromDate = value.from?.slice(0, 10);
  const toDate = value.to?.slice(0, 10);

  let mainLabel = '';
  let pKey: string | null = null;
  for (const preset of PRESETS) {
    const [pf, pt] = preset.range();
    if (pf === fromDate && pt === toDate) {
      mainLabel = preset.label;
      pKey = preset.key;
      break;
    }
  }

  if (!mainLabel) {
    if (fromDate && toDate && fromDate === toDate) mainLabel = formatShort(fromDate);
    else if (fromDate && toDate) mainLabel = `${formatShort(fromDate)} – ${formatShort(toDate)}`;
    else if (fromDate) mainLabel = `Since ${formatShort(fromDate)}`;
    else mainLabel = `Until ${formatShort(toDate!)}`;
  }

  return { label: prefix ? `${prefix}: ${mainLabel}` : mainLabel, presetKey: pKey };
}

export function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dateField, setDateField] = useState<'created' | 'assigned'>(value.dateField ?? 'created');
  const [mode, setMode] = useState<'range' | 'single'>(() => {
    const f = value.from?.slice(0, 10);
    const t = value.to?.slice(0, 10);
    return f && t && f === t ? 'single' : 'range';
  });
  const [fromInput, setFromInput] = useState(value.from?.slice(0, 10) ?? '');
  const [toInput, setToInput] = useState(value.to?.slice(0, 10) ?? (value.from ? value.from.slice(0, 10) : ''));
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const { label, presetKey } = useMemo(() => describe(value), [value]);

  function applyPreset(preset: (typeof PRESETS)[number]) {
    const [from, to] = preset.range();
    onChange({ from: startOfDayIso(from), to: endOfDayIso(to), dateField });
    setFromInput(from);
    setToInput(to);
    setMode(from === to ? 'single' : 'range');
    setOpen(false);
  }

  function applyCustom() {
    if (mode === 'single') {
      if (!fromInput) return;
      onChange({ from: startOfDayIso(fromInput), to: endOfDayIso(fromInput), dateField });
    } else {
      onChange({
        from: fromInput ? startOfDayIso(fromInput) : undefined,
        to: toInput ? endOfDayIso(toInput) : undefined,
        dateField,
      });
    }
    setOpen(false);
  }

  function clear() {
    setFromInput('');
    setToInput('');
    onChange({});
    setOpen(false);
  }

  return (
    <div className="col-picker" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn date-trigger"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        <IconCalendar width={15} height={15} />
        {label}
        <IconChevronDown width={13} height={13} />
      </button>

      {open && (
        <div className="col-popover" role="dialog" aria-label="Filter by date" style={{ width: 310 }}>
          <div className="col-popover-head">
            <strong style={{ fontSize: 13 }}>Filter leads by date</strong>
            <button type="button" className="btn btn-ghost btn-sm" onClick={clear}>
              Clear
            </button>
          </div>

          <div className="col-popover-body">
            <p className="col-group-label">Date target</p>
            <div className="seg" style={{ marginBottom: 12 }}>
              <button
                type="button"
                className={dateField === 'created' ? 'active' : ''}
                onClick={() => setDateField('created')}
              >
                Date created
              </button>
              <button
                type="button"
                className={dateField === 'assigned' ? 'active' : ''}
                onClick={() => setDateField('assigned')}
              >
                Date assigned
              </button>
            </div>

            <p className="col-group-label">Quick ranges</p>
            <div className="date-presets">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`date-preset${presetKey === p.key ? ' active' : ''}`}
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <p className="col-group-label" style={{ marginTop: 14 }}>Custom</p>
            <div className="seg" style={{ marginBottom: 10 }}>
              <button type="button" className={mode === 'range' ? 'active' : ''} onClick={() => setMode('range')}>
                Date range
              </button>
              <button type="button" className={mode === 'single' ? 'active' : ''} onClick={() => setMode('single')}>
                Specific date
              </button>
            </div>

            {mode === 'range' ? (
              <div className="grid-2">
                <div className="field">
                  <label htmlFor="date-from">From</label>
                  <input id="date-from" type="date" value={fromInput} max={toInput || undefined} onChange={(e) => setFromInput(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="date-to">To</label>
                  <input id="date-to" type="date" value={toInput} min={fromInput || undefined} onChange={(e) => setToInput(e.target.value)} />
                </div>
              </div>
            ) : (
              <div className="field">
                <label htmlFor="date-single">Date</label>
                <input
                  id="date-single"
                  type="date"
                  value={fromInput}
                  onChange={(e) => {
                    setFromInput(e.target.value);
                    setToInput(e.target.value);
                  }}
                />
              </div>
            )}

            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: '100%', marginTop: 4 }}
              disabled={mode === 'range' ? !fromInput && !toInput : !fromInput}
              onClick={applyCustom}
            >
              Apply
            </button>
          </div>

          <div className="col-popover-foot">
            Filters by when the lead was {dateField === 'assigned' ? 'assigned' : 'created'}.
          </div>
        </div>
      )}
    </div>
  );
}
